/* ==========================================================================
   VoicePad: the fully customizable AI gadget
   Behaviour for templates/page.voicepad-gadget.json.
     - glass-cube field (hero, sign-up)
     - hero: load sequence, live keys, tilt and metal highlight
     - key chapter: film frames scrubbed by scroll, job cubes flying into keys
     - everyday: keycap tabs, three demos, hold the wide key to dictate
     - your AI and Genspark demos
     - headline word reveal, top bar tone and progress, pointer label
   Everything runs only while it is on screen.
   ========================================================================== */
(function () {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  root.classList.add('vpg-js');
  window.__vpgReady = true;

  const mq = (q) => !!(window.matchMedia && matchMedia(q).matches);
  const REDUCE = mq('(prefers-reduced-motion: reduce)');
  const FINE = mq('(hover: hover) and (pointer: fine)');
  const DESIGN = !!(window.Shopify && window.Shopify.designMode);
  const STOP = { stop: true };

  /* ------------------------------------------------------------- utils */
  const $ = (s, el) => (el || doc).querySelector(s);
  const $$ = (s, el) => Array.prototype.slice.call((el || doc).querySelectorAll(s));
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (t) => { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
  const easeIO = (t) => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const hash = (x, y) => { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); };
  const swallow = (e) => { if (e !== STOP && window.console) console.error(e); };
  const inView = (el) => { const r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < (window.innerHeight || 0); };

  function onScreen(el, enter, leave, threshold, margin) {
    if (!('IntersectionObserver' in window)) { enter(); return null; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) enter(); else if (leave) leave(); });
    }, { threshold: threshold == null ? 0.2 : threshold, rootMargin: margin || '0px' });
    io.observe(el);
    return io;
  }

  /* one requestAnimationFrame loop for everything that moves with time */
  const Ticker = (() => {
    const fns = new Set();
    let raf = 0, last = 0;
    function loop(now) {
      raf = 0;
      const dt = Math.min(64, now - (last || now));
      last = now;
      fns.forEach((f) => { try { f(now, dt); } catch (e) { swallow(e); fns.delete(f); } });
      if (fns.size) raf = requestAnimationFrame(loop); else last = 0;
    }
    return {
      add(f) { fns.add(f); if (!raf) raf = requestAnimationFrame(loop); },
      remove(f) { fns.delete(f); }
    };
  })();

  /* cancellable async scripts */
  class Runner {
    constructor() { this.gen = 0; }
    token() { const g = ++this.gen; return { alive: () => this.gen === g }; }
    stop() { this.gen++; }
  }
  const wait = (tok, ms) => new Promise((res, rej) => setTimeout(() => (tok.alive() ? res() : rej(STOP)), ms));
  async function typeText(tok, el, text, speed) {
    for (let i = 1; i <= text.length; i++) {
      el.textContent = text.slice(0, i);
      const ch = text.charAt(i - 1);
      await wait(tok, speed + (ch === ' ' ? speed * .5 : 0) + (/[.,?!]/.test(ch) ? speed * 4 : 0));
    }
  }

  /* a demo that plays once per call and reports when it is done */
  function Scripted(reset, script, duration) {
    const run = new Runner();
    reset();
    return {
      duration,
      run,
      play(done) {
        run.stop(); reset();
        const tok = run.token();
        (async () => { try { await script(tok); if (done) done(); } catch (e) { swallow(e); } })();
      },
      stop() { run.stop(); reset(); }
    };
  }
  /* a demo that loops while it is on screen */
  function Looping(el, reset, script, still) {
    const d = Scripted(reset, script, 0);
    let on = false;
    function loop() { if (!on) return; d.play(() => { if (on) loop(); }); }
    const api = {
      play() { if (on) return; on = true; loop(); },
      pause() { on = false; d.stop(); },
      restart() { on = false; d.stop(); on = true; loop(); },
      destroy() { if (api.io) api.io.disconnect(); api.pause(); }
    };
    if (REDUCE) { if (still) still(); return api; }
    api.io = onScreen(el, api.play, api.pause, .35);
    return api;
  }

  /* ---------------------------------------------- headline word reveal */
  function splitWords(el) {
    if (el.classList.contains('is-split')) return;
    const label = Array.prototype.map.call(el.childNodes, (n) => (n.nodeName === 'BR' ? ' ' : n.textContent)).join('').replace(/\s+/g, ' ').trim();
    const wrap = doc.createElement('span');
    wrap.setAttribute('aria-hidden', 'true');
    let i = 0;
    const word = (node) => {
      const outer = doc.createElement('span');
      outer.className = 'vpg-w';
      const inner = doc.createElement('span');
      inner.style.setProperty('--i', i++);
      inner.appendChild(node);
      outer.appendChild(inner);
      return outer;
    };
    Array.prototype.slice.call(el.childNodes).forEach((node) => {
      if (node.nodeType === 3) {
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) wrap.appendChild(doc.createTextNode(' '));
          else wrap.appendChild(word(doc.createTextNode(part)));
        });
      } else if (node.nodeType === 1) {
        if (node.nodeName === 'BR') wrap.appendChild(doc.createElement('br'));
        else wrap.appendChild(word(node.cloneNode(true)));
      }
    });
    el.textContent = '';
    el.appendChild(wrap);
    el.setAttribute('aria-label', label);
    el.classList.add('is-split');
  }
  function reveals(scope) {
    const els = $$('[data-split]', scope);
    if (REDUCE) return;
    els.forEach((el) => {
      splitWords(el);
      const delay = parseInt(el.getAttribute('data-split-delay') || '0', 10);
      el.style.setProperty('--d0', delay + 'ms');
      if (DESIGN) { el.classList.add('is-in'); return; }
      if (el.getAttribute('data-split') === 'load') {
        requestAnimationFrame(() => setTimeout(() => el.classList.add('is-in'), 120));
        return;
      }
      if (!('IntersectionObserver' in window)) { el.classList.add('is-in'); return; }
      const io = new IntersectionObserver((en) => {
        en.forEach((e) => { if (e.isIntersecting) { el.classList.add('is-in'); io.disconnect(); } });
      }, { threshold: .25, rootMargin: '0px 0px -8% 0px' });
      io.observe(el);
    });
    $$('[data-vpg-mark]', scope).forEach((el) => {
      if (REDUCE || DESIGN) { el.classList.add('is-in'); return; }
      onScreen(el, () => el.classList.add('is-in'), null, .2);
    });
  }

  /* ------------------------------------------------------ device keys */
  function setLegend(key, text, animate) {
    const el = $('[data-legend]', key);
    if (!el || el.textContent === text) return;
    el.textContent = text;
    if (animate && !REDUCE) {
      key.classList.remove('is-flip');
      void key.offsetWidth;
      key.classList.add('is-flip');
    }
  }
  function hit(key) {
    if (!key) return;
    key.classList.add('is-hit');
    clearTimeout(key._hit);
    key._hit = setTimeout(() => key.classList.remove('is-hit'), 420);
  }

  /* ================================================== glass-cube field */
  const ICON_PATHS = [
    '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"/>',
    '<path d="M20 11.5a8 8 0 0 1-11.7 7.1L4 19.8l1.2-4A8 8 0 1 1 20 11.5z"/>',
    '<path d="M6 3h8.5L19 7.5V21H6z"/><path d="M14 3v5h5M9 12h6M9 16h6"/>',
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9.5h18M3 14.75h18M9 4v16"/>',
    '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8.5 20h7M12 16v4"/>',
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.6-3.6-9S9.6 5.6 12 3z"/>',
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.4" fill="#fff" stroke="none"/>',
    '<rect x="3" y="4" width="18" height="16" rx="3"/><path d="M7.5 9.5l3 2.5-3 2.5M12.5 15h4"/>',
    '<path d="M4 20h4L19 9l-4-4L4 16z"/><path d="M13.5 6.5l4 4"/>',
    '<path d="M4 15v-3a8 8 0 0 1 16 0v3"/><rect x="3" y="14" width="4" height="6.5" rx="1.6"/><rect x="17" y="14" width="4" height="6.5" rx="1.6"/>',
    '<path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 10v4"/>',
    '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5"/><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.6 14.9c1.6.8 2.7 2.6 3 5.1"/>'
  ];
  let ICON_IMGS = null;
  function iconImages() {
    if (ICON_IMGS) return ICON_IMGS;
    ICON_IMGS = ICON_PATHS.map((p) => {
      const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + p + '</svg>';
      const im = new Image();
      im.decoding = 'async';
      im.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      return im;
    });
    return ICON_IMGS;
  }
  const RAMP = ['#ffffff', '#dceaff', '#bad7ff', '#8fc0ff', '#5fa6ff', '#2f8cff', '#0f7fff'];
  const LALPHA = [0, .34, .5, .64, .77, .87, .94];
  const LAYOUTS = {
    heroWide: [
      { x: .80, y: .46, r: .30, a: 1, c: '#0f7fff' }, { x: .99, y: .92, r: .24, a: .95, c: '#2a63ff' },
      { x: .57, y: 1.04, r: .21, a: .7, c: '#5a6bff' }, { x: 1.02, y: .04, r: .19, a: .8, c: '#3b94ff' },
      { x: .24, y: 1.08, r: .13, a: .45, c: '#7fb5ff' }
    ],
    heroTall: [
      { x: .52, y: .97, r: .5, a: 1, c: '#0f7fff' }, { x: 1.04, y: .66, r: .3, a: .85, c: '#2a63ff' },
      { x: -.06, y: .8, r: .3, a: .62, c: '#5a6bff' }, { x: .98, y: .02, r: .2, a: .5, c: '#3b94ff' }
    ],
    endWide: [
      { x: .80, y: .52, r: .29, a: 1, c: '#0f7fff' }, { x: 1.02, y: .08, r: .22, a: .85, c: '#2a63ff' },
      { x: .6, y: 1.02, r: .23, a: .72, c: '#5a6bff' }
    ],
    endTall: [
      { x: .55, y: .98, r: .46, a: 1, c: '#0f7fff' }, { x: 1.04, y: .6, r: .28, a: .8, c: '#2a63ff' },
      { x: -.04, y: .84, r: .26, a: .58, c: '#5a6bff' }
    ]
  };
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function Field(host) {
    const kind = host.getAttribute('data-vpg-field') || 'hero';
    const useIcons = host.getAttribute('data-icons') === '1';
    const section = host.parentNode;
    const tide = doc.createElement('canvas');
    const base = doc.createElement('canvas');
    const tiles = doc.createElement('canvas');
    tide.className = 'vpg-field__tide';
    host.appendChild(tide);
    host.appendChild(base);
    host.appendChild(tiles);
    host.classList.add('is-live');
    const tctx = tide.getContext('2d');
    const fctx = tiles.getContext('2d');
    const bctx = base.getContext('2d');
    const canBlur = typeof fctx.filter === 'string';

    let W = 0, H = 0, S = 1, dpr = 1, G = 52, cols = 0, rows = 0, TW = 0, TH = 0, TOY = 0, crop = 0;
    let layout = [], vals = null, levels = null, sprites = [], hot = null, built = '';
    let visible = false, raf = 0, born = 0, introT = REDUCE ? 1 : 0, last = 0, clock = 0, baseAt = -1e9;
    const pointer = { x: 0, y: 0, inside: false, touched: false, t: 0 };
    const field = { x: 0, y: 0, open: 0 };
    const ripples = [];
    const icons = useIcons ? iconImages() : null;
    let iconLv = null, iconKey = '', iconTiles = [], clar = null, amb = null;
    const self = this;

    function resize() {
      const r = host.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width));
      H = Math.max(1, Math.round(r.height));
      S = Math.max(W, H);
      dpr = Math.min(window.devicePixelRatio || 1, W < 600 ? 1.5 : 2);
      G = W < 600 ? 40 : W < 990 ? 46 : 52;
      const wide0 = W >= 990 && W / H > 1.05;
      /* on tall screens the drifting light and the icons stay in the lower part, so the
         per-frame canvas only covers that part (the whole field while a ripple runs) */
      crop = wide0 ? 0 : Math.round(H * .4);
      base.width = Math.round(W * dpr);
      base.height = Math.round(H * dpr);
      TOY = -1;
      setTop(crop);
      TW = Math.max(60, Math.round(W / 8));
      TH = Math.max(60, Math.round(H / 8));
      tide.width = TW; tide.height = TH;
      cols = Math.ceil(W / G) + 1;
      rows = Math.ceil(H / G) + 1;
      vals = new Float32Array(cols * rows);
      levels = new Uint8Array(cols * rows);
      clar = new Float32Array(cols * rows);
      amb = new Float64Array(cols * rows);
      const wide = W >= 990 && W / H > 1.05;
      layout = LAYOUTS[(kind === 'end' ? 'end' : 'hero') + (wide ? 'Wide' : 'Tall')];
      buildSprites();
      baseAt = -1e9;
      if (!pointer.touched) { field.x = W * (wide ? .78 : .5); field.y = H * (wide ? .46 : .86); }
      if (!visible || REDUCE) draw(performance.now());
    }

    function setTop(v) {
      if (v === TOY) return;
      TOY = v;
      tiles.width = Math.round(W * dpr);
      tiles.height = Math.max(1, Math.round((H - v) * dpr));
      tiles.style.top = v + 'px';
      tiles.style.height = (H - v) + 'px';
    }
    function buildSprites() {
      const key = G + ':' + dpr;
      if (key === built) return;
      built = key;
      const s = Math.max(4, Math.round(G * dpr));
      const gap = Math.max(1, Math.round(2.2 * dpr));
      const rad = 5 * dpr;
      sprites = RAMP.map((col) => {
        const c = doc.createElement('canvas');
        c.width = c.height = s;
        const x = c.getContext('2d');
        x.fillStyle = col;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad); x.fill();
        const sheen = x.createLinearGradient(0, 0, s, s);
        sheen.addColorStop(0, 'rgba(255,255,255,0.30)');
        sheen.addColorStop(.45, 'rgba(255,255,255,0.05)');
        sheen.addColorStop(1, 'rgba(255,255,255,0)');
        x.fillStyle = sheen;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad); x.fill();
        const shade = x.createLinearGradient(s, s, 0, 0);
        shade.addColorStop(0, 'rgba(8,30,80,0.12)');
        shade.addColorStop(.5, 'rgba(8,30,80,0)');
        x.fillStyle = shade;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad); x.fill();
        x.strokeStyle = 'rgba(255,255,255,0.35)';
        x.lineWidth = Math.max(1, dpr * .8);
        roundRect(x, gap + .5 * dpr, gap + .5 * dpr, s - gap * 2 - dpr, s - gap * 2 - dpr, rad); x.stroke();
        return c;
      });
      const HS = 128;
      hot = doc.createElement('canvas');
      hot.width = hot.height = HS;
      const h = hot.getContext('2d');
      const hp = h.createRadialGradient(HS / 2, HS / 2, HS * .18, HS / 2, HS / 2, HS * .56);
      hp.addColorStop(0, 'rgba(255,255,255,0)');
      hp.addColorStop(.6, 'rgba(255,255,255,0.12)');
      hp.addColorStop(1, 'rgba(255,255,255,0.5)');
      h.fillStyle = hp; roundRect(h, 0, 0, HS, HS, HS * .1); h.fill();
      const hs = h.createLinearGradient(0, 0, HS, HS);
      hs.addColorStop(0, 'rgba(255,255,255,0.38)');
      hs.addColorStop(.5, 'rgba(255,255,255,0)');
      hs.addColorStop(1, 'rgba(8,30,80,0.2)');
      h.fillStyle = hs; roundRect(h, 0, 0, HS, HS, HS * .1); h.fill();
      iconKey = '';
    }
    function blobPos(b, i, t) {
      return {
        x: (b.x + Math.sin(t * (.07 + i * .013) + i * 1.7) * .035) * W,
        y: (b.y + Math.cos(t * (.055 + i * .011) + i * 2.3) * .04) * H,
        r: b.r * S * (1 + Math.sin(t * .09 + i) * .06)
      };
    }
    function landed(gx, gy) {
      if (introT >= 1) return 1;
      const d = ((cols - 1 - gx) / Math.max(1, cols)) * .6 + ((rows - 1 - gy) / Math.max(1, rows)) * .4;
      return smooth(introT * 1.9 - d * .85);
    }
    function hexA(hex, a) {
      const n = parseInt(hex.slice(1), 16);
      return 'rgba(' + (n >> 16 & 255) + ',' + (n >> 8 & 255) + ',' + (n & 255) + ',' + a.toFixed(3) + ')';
    }
    function rebuild(t) {
      tctx.fillStyle = '#ffffff';
      tctx.fillRect(0, 0, TW, TH);
      const kx = TW / W, ky = TH / H;
      const P = layout.map((b, i) => blobPos(b, i, t));
      layout.forEach((b, i) => {
        const p = P[i];
        const g = tctx.createRadialGradient(p.x * kx, p.y * ky, 0, p.x * kx, p.y * ky, p.r * 1.45 * kx);
        g.addColorStop(0, hexA(b.c, .62 * b.a));
        g.addColorStop(.55, hexA(b.c, .22 * b.a));
        g.addColorStop(1, hexA(b.c, 0));
        tctx.fillStyle = g;
        tctx.fillRect(0, 0, TW, TH);
      });
      for (let gx = 0; gx < cols; gx++) {
        for (let gy = 0; gy < rows; gy++) {
          const cx = gx * G + G / 2, cy = gy * G + G / 2;
          let v = 0;
          for (let i = 0; i < P.length; i++) {
            const dx = (cx - P[i].x) / P[i].r, dy = (cy - P[i].y) / P[i].r;
            v += layout[i].a * Math.exp(-(dx * dx + dy * dy) * 1.9);
          }
          v = clamp(v, 0, 1);
          const k = gy * cols + gx;
          vals[k] = v;
          levels[k] = Math.min(6, Math.floor(v * 7.4));
        }
      }
      bctx.setTransform(1, 0, 0, 1, 0, 0);
      bctx.clearRect(0, 0, base.width, base.height);
      const s = G * dpr;
      for (let gx = 0; gx < cols; gx++) {
        for (let gy = 0; gy < rows; gy++) {
          const L = levels[gy * cols + gx];
          if (L < 1) continue;
          const a = LALPHA[L] * landed(gx, gy);
          if (a < .01) continue;
          bctx.globalAlpha = a;
          bctx.drawImage(sprites[L], gx * s, gy * s, s, s);
        }
      }
      bctx.globalAlpha = 1;
      pickIconTiles();
    }
    function buildIcons() {
      if (!icons) return;
      const key = G + ':' + dpr;
      if (key !== iconKey) { iconKey = key; iconLv = icons.map(() => null); }
      const box = Math.round(G * .44 * dpr), pad = Math.ceil(6 * dpr * 2), side = box + pad * 2, LV = 5;
      let budget = 2;
      iconLv = iconLv.map((lv, n) => {
        if (lv || budget <= 0) return lv;
        const im = icons[n];
        if (!(im.complete && im.naturalWidth)) return null;
        budget--;
        const out = [];
        for (let k = 0; k < LV; k++) {
          const r = 6 * dpr * k / (LV - 1);
          const c = doc.createElement('canvas');
          c.width = c.height = side;
          const x = c.getContext('2d');
          if (r < .1) x.drawImage(im, pad, pad, box, box);
          else if (canBlur) { x.filter = 'blur(' + r.toFixed(2) + 'px)'; x.drawImage(im, pad, pad, box, box); x.filter = 'none'; }
          else {
            const q = Math.max(3, Math.round(box / (1 + r * .9)));
            const tmp = doc.createElement('canvas');
            tmp.width = tmp.height = q;
            tmp.getContext('2d').drawImage(im, 0, 0, q, q);
            x.drawImage(tmp, 0, 0, q, q, pad - r * .5, pad - r * .5, box + r, box + r);
          }
          out.push(c);
        }
        out.side = side / dpr;
        return out;
      });
    }
    function pickIconTiles() {
      if (!icons) return;
      iconTiles = [];
      for (let gx = 0; gx < cols; gx++) {
        for (let gy = 0; gy < rows; gy++) {
          const k = gy * cols + gx;
          if (levels[k] < 4) continue;
          if (hash(gx * 1.7 + 3.1, gy * 2.3 + 7.7) > .38) continue;
          iconTiles.push({ k, gx, gy, w: vals[k] * landed(gx, gy), ic: Math.floor(hash(gx + 17.3, gy - 9.1) * icons.length) % icons.length });
        }
      }
    }
    function waveAt(x, y) {
      let v = 0;
      const band = G * 1.5;
      for (let i = 0; i < ripples.length; i++) {
        const rp = ripples[i];
        const d = Math.sqrt((x - rp.x) * (x - rp.x) + (y - rp.y) * (y - rp.y));
        const b = 1 - Math.abs(d - rp.r) / band;
        if (b > 0) v = Math.max(v, b * (1 - rp.r / rp.max));
      }
      return v;
    }
    function draw(now) {
      const dt = Math.min(64, now - (last || now));
      last = now;
      if (!REDUCE) clock += dt / 1000;
      if (introT < 1 && born) introT = clamp((now - born - 200) / 1500, 0, 1);
      /* the colour wash drifts a few px per second, so the cube layer is redrawn a few times a second */
      if (introT < 1 || now - baseAt > (W < 600 ? 320 : 240)) { baseAt = now; rebuild(clock); }

      let tx, ty;
      const wide = W >= 990 && W / H > 1.05;
      if (pointer.inside) { tx = pointer.x; ty = pointer.y; }
      else {
        const t = now / 1000;
        tx = W * (wide ? .74 + Math.sin(t * .43) * .17 : .5 + Math.sin(t * .43) * .32);
        ty = H * (wide ? .5 + Math.sin(t * .71 + 1.1) * .2 : .82 + Math.sin(t * .71 + 1.1) * .1);
        if (REDUCE) { tx = W * .72; ty = H * .5; }
      }
      const f = 1 - Math.pow(1 - .12, dt / 16.667);
      field.x += (tx - field.x) * f;
      field.y += (ty - field.y) * f;
      field.open += (1 - field.open) * Math.min(1, dt / 260);
      for (let q = ripples.length - 1; q >= 0; q--) {
        ripples[q].r = (now - ripples[q].t0) * ripples[q].speed;
        if (ripples[q].r > ripples[q].max) ripples.splice(q, 1);
      }

      setTop(ripples.length || pointer.inside ? 0 : crop);
      fctx.setTransform(1, 0, 0, 1, 0, 0);
      fctx.clearRect(0, 0, tiles.width, tiles.height);
      fctx.setTransform(dpr, 0, 0, dpr, 0, -TOY * dpr);

      const R = (W < 600 ? 190 : 280) * field.open;
      const gx0 = Math.max(0, Math.floor((field.x - R) / G)), gx1 = Math.min(cols - 1, Math.floor((field.x + R) / G));
      const gy0 = Math.max(0, Math.floor((field.y - R) / G)), gy1 = Math.min(rows - 1, Math.floor((field.y + R) / G));
      for (let gx = gx0; gx <= gx1; gx++) {
        for (let gy = gy0; gy <= gy1; gy++) {
          const cx = gx * G + G / 2, cy = gy * G + G / 2;
          const n = Math.sqrt((cx - field.x) * (cx - field.x) + (cy - field.y) * (cy - field.y)) / R;
          if (n >= 1) continue;
          let l = 1 - n;
          l += .07 * Math.sin(n * Math.PI * 2.6 - clock * 2.2) * l;
          l += .12 * (hash(gx, gy) - .5) * 2 * l;
          l = smooth(l);
          const k = gy * cols + gx;
          const a = l * Math.max(vals[k], .3) * landed(gx, gy);
          if (a < .02) continue;
          const L = Math.min(6, Math.max(2, levels[k] + 1));
          const half = (G / 2) * (1 + .16 * l);
          fctx.globalAlpha = a;
          fctx.drawImage(sprites[L], cx - half, cy - half, half * 2, half * 2);
          if (l > .12) { fctx.globalAlpha = a * .9; fctx.drawImage(hot, cx - half, cy - half, half * 2, half * 2); }
        }
      }
      if (ripples.length) {
        for (let gx = 0; gx < cols; gx++) {
          for (let gy = 0; gy < rows; gy++) {
            const k = gy * cols + gx;
            if (vals[k] < .12) continue;
            const wx = gx * G + G / 2, wy = gy * G + G / 2, wv = waveAt(wx, wy);
            if (wv < .03) continue;
            const hh = (G / 2) * (1 + .12 * wv);
            fctx.globalAlpha = wv * vals[k] * .9;
            fctx.drawImage(sprites[Math.min(6, levels[k] + 1)], wx - hh, wy - hh, hh * 2, hh * 2);
            fctx.drawImage(hot, wx - hh, wy - hh, hh * 2, hh * 2);
          }
        }
      }
      if (icons) {
        buildIcons();
        if (!REDUCE && Math.random() < dt / 700 && iconTiles.length) {
          const pick = iconTiles[(Math.random() * iconTiles.length) | 0];
          amb[pick.k] = now + 1500 + Math.random() * 1500;
        }
        const thaw = 1 - Math.pow(1 - .16, dt / 16.667), freeze = 1 - Math.pow(1 - .05, dt / 16.667);
        for (let m = 0; m < iconTiles.length; m++) {
          const p = iconTiles[m], lv = iconLv && iconLv[p.ic];
          if (!lv) continue;
          const icx = p.gx * G + G / 2, icy = p.gy * G + G / 2;
          const near = R > 1 ? smooth(1 - Math.sqrt((icx - field.x) * (icx - field.x) + (icy - field.y) * (icy - field.y)) / R) : 0;
          const target = REDUCE ? (hash(p.gx + 5.5, p.gy + 1.3) < .35 ? .7 : 0)
            : Math.max(smooth((near - .2) / .55), now < amb[p.k] ? 1 : 0, ripples.length ? Math.min(1, waveAt(icx, icy) * 1.6) : 0);
          let c = clar[p.k];
          c += (target - c) * (target > c ? thaw : freeze);
          clar[p.k] = c;
          const alpha = p.w * (.26 + .74 * c);
          if (alpha < .02) continue;
          const sc = (.86 + .14 * c) * (1 + .16 * near);
          const sd = lv.side * sc;
          const fl = (1 - c) * (lv.length - 1), k0 = Math.floor(fl), k1 = Math.min(lv.length - 1, k0 + 1), tt = fl - k0;
          fctx.globalAlpha = alpha * (1 - tt);
          fctx.drawImage(lv[k0], icx - sd / 2, icy - sd / 2, sd, sd);
          if (tt > .01) { fctx.globalAlpha = alpha * tt; fctx.drawImage(lv[k1], icx - sd / 2, icy - sd / 2, sd, sd); }
        }
      }
      fctx.globalAlpha = 1;
      fctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    let lastDraw = 0;
    function frame(now) {
      raf = 0;
      if (!visible) return;
      /* full rate only while the pointer is moving, a ripple runs or the field is landing;
         30 fps otherwise, 24 on phones */
      const busy = W >= 600 && (now - pointer.t < 600 || ripples.length || introT < 1);
      if (busy || now - lastDraw > (W < 600 ? 38 : 30)) { lastDraw = now; draw(now); }
      raf = requestAnimationFrame(frame);
    }
    function start() {
      if (visible) return;
      visible = true;
      if (!born) born = performance.now();
      last = 0;
      if (REDUCE) { draw(performance.now()); return; }
      if (!raf) raf = requestAnimationFrame(frame);
    }
    function stop() { visible = false; if (raf) cancelAnimationFrame(raf); raf = 0; }

    this.ripple = (clientX, clientY, big) => {
      if (REDUCE) return;
      const r = host.getBoundingClientRect();
      ripples.push({ x: clientX - r.left, y: clientY - r.top, t0: performance.now(), r: 0, speed: big ? 1.1 : .85, max: big ? Math.sqrt(W * W + H * H) : G * 8 });
      if (ripples.length > 6) ripples.shift();
    };
    const move = (e) => {
      const r = host.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      pointer.inside = x >= 0 && y >= 0 && x <= r.width && y <= r.height && e.pointerType !== 'touch';
      if (pointer.inside) { pointer.x = x; pointer.y = y; pointer.touched = true; pointer.t = performance.now(); }
    };
    const tap = (e) => {
      const t = e.target;
      if (t && t.closest && t.closest('a,button,input,label,select,textarea,form,.vpg-dev')) return;
      self.ripple(e.clientX, e.clientY, false);
    };
    window.addEventListener('pointermove', move, { passive: true });
    section.addEventListener('pointerdown', tap, { passive: true });
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => resize()) : null;
    if (ro) ro.observe(host); else window.addEventListener('resize', resize);
    resize();
    this.io = onScreen(host, start, stop, 0);
    doc.addEventListener('visibilitychange', () => { if (doc.hidden) stop(); else if (inView(host)) start(); });
    this.destroy = () => { stop(); if (this.io) this.io.disconnect(); if (ro) ro.disconnect(); window.removeEventListener('pointermove', move); };
  }

  /* ============================================================= hero */
  const POOLS = [
    ['Agent', 'ChatGPT', 'Claude', 'Gemini', 'Grok'],
    ['Meeting', 'Terminal', 'Rewrite'],
    ['Translate', 'Polish', 'Rewrite'],
    ['Memo', 'Hands-free'],
    ['Dictate', 'Push to talk']
  ];
  function Hero(sec, field) {
    const stage = $('[data-vpg-tilt]', sec);
    const dev = stage && $('.vpg-dev', stage);
    if (!dev) return null;
    const keys = $$('.vpg-key', dev);
    const sheen = $('.vpg-dev__sheen', dev);
    const pools = POOLS.map((p) => p.slice());
    const pos = keys.map((k, i) => {
      const cur = $('[data-legend]', k).textContent.trim();
      let at = pools[i].indexOf(cur);
      if (at < 0) { pools[i].unshift(cur); at = 0; }
      return at;
    });
    let visible = false, timer = 0, step = 0, hover = -1, booted = false;
    const order = [4, 0, 1, 2, 3];
    function light(i, ms) {
      const k = keys[i];
      k.classList.add('is-down');
      setTimeout(() => { k.classList.remove('is-down'); k.classList.add('is-lit'); }, 110);
      setTimeout(() => { if (hover !== i) k.classList.remove('is-lit'); }, ms || 1150);
    }
    function next(i) { pos[i] = (pos[i] + 1) % pools[i].length; setLegend(keys[i], pools[i][pos[i]], true); }
    function tick() {
      if (!visible) return;
      const i = order[step % order.length];
      light(i);
      if (step >= 5 && step % 2 === 1) setTimeout(() => next(i), 260);
      step++;
      timer = setTimeout(tick, 1500);
    }
    function boot() {
      if (booted) return;
      booted = true;
      if (REDUCE) { keys[4].classList.add('is-lit'); return; }
      order.forEach((i, n) => setTimeout(() => { keys[i].classList.add('is-lit'); setTimeout(() => keys[i].classList.remove('is-lit'), 560); }, n * 150));
      setTimeout(() => {
        const r = dev.getBoundingClientRect();
        if (field) field.ripple(r.left + r.width / 2, r.top + r.height * .6, true);
      }, 520);
      timer = setTimeout(tick, 2400);
    }
    keys.forEach((k, i) => {
      k.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hover = i; k.classList.add('is-lit'); } });
      k.addEventListener('pointerleave', () => { if (hover === i) hover = -1; k.classList.remove('is-lit'); });
      k.addEventListener('click', () => {
        light(i, 900);
        next(i);
        if (field) { const r = k.getBoundingClientRect(); field.ripple(r.left + r.width / 2, r.top + r.height / 2, true); }
      });
    });

    /* tilt and the highlight on the metal */
    let tx = 0, ty = 0, x = 0, y = 0, ticking = false;
    const auto = !FINE;
    function apply(now) {
      if (auto) { tx = Math.sin(now / 1700) * .32; ty = Math.cos(now / 2300) * .2; }
      const k = .085;
      x += (tx - x) * k; y += (ty - y) * k;
      dev.style.transform = 'perspective(1100px) rotateX(' + (-y * 11).toFixed(2) + 'deg) rotateY(' + (x * 15).toFixed(2) + 'deg) rotateZ(-8deg)';
      if (sheen) sheen.style.transform = 'translate3d(' + (-66.667 * clamp(.5 - x * 1.1 + y * .35, 0, 1)).toFixed(2) + '%,0,0)';
      if (!auto && Math.abs(tx - x) < .001 && Math.abs(ty - y) < .001) { Ticker.remove(apply); ticking = false; }
    }
    function wake() { if (!ticking && !REDUCE) { ticking = true; Ticker.add(apply); } }
    if (FINE && !REDUCE) {
      sec.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const r = sec.getBoundingClientRect();
        tx = (e.clientX - r.left) / r.width - .5; ty = (e.clientY - r.top) / r.height - .5;
        wake();
      }, { passive: true });
      sec.addEventListener('pointerleave', () => { tx = 0; ty = 0; wake(); });
    }

    const io = onScreen(sec, () => {
      if (visible) return;
      visible = true;
      if (!booted) setTimeout(boot, 1150); else timer = setTimeout(tick, 900);
      if (auto) wake();
    }, () => {
      visible = false; clearTimeout(timer);
      if (auto) { Ticker.remove(apply); ticking = false; }
    }, .1);
    return { destroy() { visible = false; clearTimeout(timer); Ticker.remove(apply); if (io) io.disconnect(); } };
  }

  /* ====================================================== key chapter */
  const KEYBOX = [[7.15, 23.33, 39.78, 21.96], [52.47, 23.33, 39.73, 21.96], [7.15, 48.38, 39.78, 21.96], [52.42, 48.38, 39.78, 21.99], [8.26, 73.44, 82.88, 21.99]];
  function Chapter(sec) {
    const pin = $('.vpg-ch__pin', sec);
    const film = $('.vpg-ch__film', sec);
    const canvas = $('.vpg-ch__canvas', sec);
    const cctx = canvas.getContext('2d');
    const t1 = $('.vpg-ch__t1', sec), t2 = $('.vpg-ch__t2', sec);
    const stage = $('[data-stage]', sec);
    const devWrap = $('[data-device]', sec);
    const dev = $('.vpg-dev', devWrap);
    const keys = $$('.vpg-key', dev);
    const glow = $('.vpg-ch__glow', sec);
    const tilesBox = $('[data-tiles]', sec);
    const copy = $('.vpg-ch__copy', sec);
    const caps = $$('.vpg-ch__cap', sec);
    const dots = $$('.vpg-ch__dots li', sec);
    const setupCaps = caps.filter((c) => c.getAttribute('data-cap') !== 'end');
    const setups = setupCaps.map((c) => (c.getAttribute('data-keys') || '').split('|').map((s) => s.trim()));
    const N = setups.length;
    if (!N || !dev) return null;

    if (REDUCE) {
      sec.classList.add('is-static');
      keys.forEach((k, i) => setLegend(k, setups[0][i] || '', false));
      return null;
    }

    const F = .17, S0 = .215, END = .8, STAG = .013;
    const SEG = (END - S0) / N;
    const ORDER = [4, 0, 1, 2, 3];
    const tiles = $$('.vpg-tile', sec).map((el, i) => ({
      el, i, job: el.getAttribute('data-job'), x: 0, y: 0, s: 1, o: 0,
      home: { x: 0, y: 0, s: 1, o: 1, z: 0 }, dest: { kind: 'home' }, tw: null, arrived: true,
      ph: hash(i * 3.1, i * 1.7) * 6.283
    }));
    const byJob = {};
    tiles.forEach((t) => { byJob[t.job] = t; });
    keys.forEach((k) => setLegend(k, '', false));
    const current = [null, null, null, null, null];
    let custom = null, sel = 0, play = false;

    let W = 0, H = 0, cx = 0, cy = 0, dw = 0, dh = 0, TS = 96, keyPos = [];
    let p = 0, sp = 0, visible = false, revealAt = 0, dpr = 1;
    let mx = 0, my = 0, pmx = 0, pmy = 0;

    function layout() {
      W = pin.clientWidth; H = pin.clientHeight;
      dw = devWrap.offsetWidth; dh = devWrap.offsetHeight;
      cx = devWrap.offsetLeft; cy = devWrap.offsetTop;
      const left = cx - dw / 2, top = cy - dh / 2;
      keyPos = KEYBOX.map((b) => ({ x: left + (b[0] + b[2] / 2) / 100 * dw, y: top + (b[1] + b[3] / 2) / 100 * dh, w: b[2] / 100 * dw, h: b[3] / 100 * dh }));
      glow.style.setProperty('--gx', cx + 'px');
      glow.style.setProperty('--gy', cy + 'px');
      const vis = tiles.filter((t) => t.el.offsetParent !== null);
      TS = vis.length ? vis[0].el.offsetWidth : 96;
      placeHomes(vis);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      drawn = -1;
    }

    function placeHomes(vis) {
      const n = vis.length;
      if (!n) return;
      const pr = pin.getBoundingClientRect();
      const cr = copy.getBoundingClientRect();
      const wide = W >= 990;
      /* keep tiles off the captions: the caption column on wide screens,
         the band above the tallest caption on narrow ones */
      let capH = 0;
      caps.forEach((c) => { capH = Math.max(capH, c.offsetHeight); });
      const ex = wide
        ? { r: cr.right - pr.left + TS * .35, t: 1e4 }
        : { r: -1e4, t: cr.bottom - pr.top - capH - TS * .3 };
      const navH = W >= 750 ? 68 : 60;
      const m = TS * .6 + 8;
      const top = navH + TS * .62 + 10;
      const pad = TS * .68;
      const g = Math.max(10, TS * .2);
      const cand = [];
      for (let y = top; y <= H - m; y += g) {
        for (let x = m; x <= W - m; x += g) {
          if (wide && x - TS * .5 < ex.r) continue;
          if (!wide && y + TS * .5 > ex.t) continue;
          if (Math.abs(x - cx) < dw / 2 + pad && Math.abs(y - cy) < dh / 2 + pad) continue;
          cand.push({ x, y, d: Math.hypot((x - cx) / (dw * .5 + TS), (y - cy) / (dh * .5 + TS)), n: hash(x * .37, y * .71) });
        }
      }
      const extras = vis.filter((t) => t.el.classList.contains('vpg-tile--extra'));
      const order = vis.filter((t) => extras.indexOf(t) < 0).concat(extras);
      const need = n - extras.length;
      let picked = [];
      /* spread the spots out (farthest-point sampling with a hard minimum gap, so
         tiles never overlap even while they bob); loosen the gap only if needed */
      for (const f of [1.3, 1.18, 1.06]) {
        const minD = TS * f + 6;
        picked = [];
        while (picked.length < n) {
          let best = null, bs = -1e9;
          for (const c of cand) {
            let md = 1e9;
            for (const q of picked) {
              const dd = Math.hypot(c.x - q.x, c.y - q.y);
              if (dd < md) { md = dd; if (md < minD) break; }
            }
            if (md < minD) continue;
            const sc = Math.min(md, TS * 2.5) - c.d * TS * .55 + c.n * TS * .3;
            if (sc > bs) { bs = sc; best = c; }
          }
          if (!best) break;
          picked.push(best);
        }
        if (picked.length >= need) break;
      }
      /* nearest spots go to the jobs the setups use; extra jobs only if there is room */
      const spots = picked.slice().sort((a, b) => a.d - b.d);
      order.forEach((t, i) => {
        let c = spots[i];
        /* no room left (extras first, then anything on very small screens): the tile stays hidden at home */
        t.off = !c;
        if (!c) c = { x: cx + Math.cos(i * 2.4) * (dw * .5 + TS * .9), y: cy + Math.sin(i * 2.4) * (dh * .5 + TS * .3), d: 2.2 };
        const z = c.d < 1.4 ? 0 : c.d < 2.1 ? 1 : 2;
        t.home = { x: c.x, y: c.y, s: [1, .9, .8][z], o: t.off ? 0 : [1, .88, .7][z], z };
        if (!t.tw && t.dest.kind === 'home') { t.x = c.x; t.y = c.y; t.s = t.home.s; }
      });
    }

    function homePos(t, now) {
      const h = t.home, ts = now / 1000;
      const par = (1 - h.z * .35);
      const amp = play ? .45 : 1;
      return {
        x: h.x + Math.sin(ts * .55 + t.ph) * TS * .07 * amp + pmx * 16 * par,
        y: h.y + Math.cos(ts * .47 + t.ph * 1.3) * TS * .09 * amp + pmy * 12 * par,
        s: h.s, o: h.o
      };
    }
    function keyTarget(k) {
      const kp = keyPos[k];
      return { x: kp.x, y: kp.y, s: Math.min(kp.w, kp.h) / TS * .96, o: 0 };
    }
    function retarget(t, dest, now) {
      const to = dest.kind === 'key' ? keyTarget(dest.k) : homePos(t, now);
      const dx = to.x - t.x, dy = to.y - t.y, dist = Math.hypot(dx, dy) || 1;
      const side = hash(t.i + 1, (dest.k || 0) + 7) > .5 ? 1 : -1;
      t.tw = { fx: t.x, fy: t.y, fs: t.s, fo: t.o, t0: now, dur: 640 + Math.min(560, dist * .55), cx: -dy / dist * dist * .2 * side, cy: dx / dist * dist * .2 * side };
      t.dest = dest;
      t.arrived = false;
    }
    function land(k, job) {
      setLegend(keys[k], job || '', true);
      if (job) hit(keys[k]);
    }
    function mappingAt(q) {
      if (q >= END) { if (!custom) custom = setups[N - 1].slice(); return custom; }
      const m = [null, null, null, null, null];
      for (let i = 0; i < N; i++) {
        const st = S0 + i * SEG;
        for (let j = 0; j < 5; j++) {
          const k = ORDER[j];
          if (q >= st + j * STAG) m[k] = setups[i][k] || null;
        }
      }
      return m;
    }
    function applyMapping(m, now) {
      for (let k = 0; k < 5; k++) {
        if (m[k] === current[k]) continue;
        const old = current[k];
        current[k] = m[k];
        if (m[k]) {
          const t = byJob[m[k]];
          if (t && t.el.offsetParent !== null) retarget(t, { kind: 'key', k }, now);
          else land(k, m[k]);
        } else {
          land(k, '');
        }
        if (old && m.indexOf(old) < 0) {
          const t = byJob[old];
          if (t && !(t.dest.kind === 'key' && m[t.dest.k] === old)) retarget(t, { kind: 'home' }, now);
        }
      }
    }
    function stepTile(t, now) {
      const dest = t.dest;
      const to = dest.kind === 'key' ? keyTarget(dest.k) : homePos(t, now);
      let intro = 1;
      if (revealAt) intro = easeIO(clamp((now - revealAt - t.i * 45) / 700, 0, 1));
      if (!t.tw) {
        t.x = to.x; t.y = to.y; t.s = to.s; t.o = to.o;
      } else {
        const tw = t.tw;
        const u = clamp((now - tw.t0) / tw.dur, 0, 1);
        const e = easeIO(u);
        const mx2 = (tw.fx + to.x) / 2 + tw.cx, my2 = (tw.fy + to.y) / 2 + tw.cy;
        const a = (1 - e) * (1 - e), b = 2 * (1 - e) * e, c = e * e;
        t.x = a * tw.fx + b * mx2 + c * to.x;
        t.y = a * tw.fy + b * my2 + c * to.y;
        t.s = tw.fs + (to.s - tw.fs) * e;
        if (dest.kind === 'key') {
          t.o = u < .78 ? Math.min(1, tw.fo + (1 - tw.fo) * Math.min(1, u / .16)) : Math.max(0, 1 - (u - .78) / .18);
          if (!t.arrived && u >= .86) { t.arrived = true; land(dest.k, t.job); }
        } else {
          t.o = tw.fo + (to.o - tw.fo) * Math.min(1, u / .3);
        }
        if (u >= 1) t.tw = null;
      }
      const s = t.s * (dest.kind === 'home' ? (.55 + .45 * intro) : 1);
      const o = t.o * (dest.kind === 'home' ? intro : 1);
      t.el.style.transform = 'translate3d(' + (t.x - TS / 2).toFixed(1) + 'px,' + (t.y - TS / 2).toFixed(1) + 'px,0) scale(' + s.toFixed(3) + ')';
      t.el.style.opacity = o.toFixed(3);
      t.el.style.zIndex = dest.kind === 'key' ? 5 : 3 - t.home.z;
      const parked = !t.tw && ((t.off && dest.kind === 'home') || dest.kind === 'key');
      t.el.style.visibility = parked ? 'hidden' : '';
    }

    /* film frames */
    const data = JSON.parse(($('[data-frames]', sec) || { textContent: '{}' }).textContent || '{}');
    let imgs = [], ready = [], setName = '', drawn = -1, filmOn = true;
    function loadFrames() {
      const portrait = (pin.clientWidth / Math.max(1, pin.clientHeight)) < .9;
      const name = portrait ? 'm' : 'd';
      const urls = data[name] || [];
      if (name === setName || !urls.length) return;
      setName = name;
      ready = urls.map(() => false);
      imgs = urls.map((u, i) => {
        const im = new Image();
        im.decoding = 'async';
        im.src = u;
        /* decode off the main thread before the first draw */
        const ok = () => { ready[i] = true; drawn = -1; };
        if (im.decode) im.decode().then(ok, () => { im.onload = ok; if (im.complete && im.naturalWidth) ok(); });
        else im.onload = ok;
        return im;
      });
    }
    function drawFrame(idx) {
      if (!imgs.length) return;
      let use = -1;
      for (let d = 0; d < imgs.length; d++) {
        if (idx - d >= 0 && ready[idx - d]) { use = idx - d; break; }
        if (idx + d < imgs.length && ready[idx + d]) { use = idx + d; break; }
      }
      if (use < 0 || use === drawn) return;
      const im = imgs[use];
      const cw = canvas.width, ch = canvas.height;
      const sc = Math.max(cw / im.naturalWidth, ch / im.naturalHeight);
      const w = im.naturalWidth * sc, h = im.naturalHeight * sc;
      cctx.drawImage(im, (cw - w) / 2, (ch - h) / 2, w, h);
      if (drawn < 0) film.style.backgroundImage = 'none';
      drawn = use;
    }

    function rawProgress() {
      const r = sec.getBoundingClientRect();
      const total = r.height - pin.offsetHeight;
      return total > 0 ? clamp(-r.top / total, 0, 1) : 0;
    }
    function capOpacity(q, a, b, last) {
      const fi = smooth((q - a) / .03);
      const fo = last ? 1 : 1 - smooth((q - (b - .03)) / .03);
      return fi * fo;
    }
    function setPlay(on) {
      if (on === play) return;
      play = on;
      sec.classList.toggle('is-play', on);
      tilesBox.setAttribute('aria-hidden', on ? 'false' : 'true');
      tiles.forEach((t) => {
        t.el.tabIndex = on ? 0 : -1;
        if (on) t.el.setAttribute('data-cursor', 'Assign'); else t.el.removeAttribute('data-cursor');
      });
      keys.forEach((k, i) => {
        k.tabIndex = on ? 0 : -1;
        k.classList.toggle('is-sel', on && i === sel);
        k.setAttribute('aria-pressed', on && i === sel ? 'true' : 'false');
        if (on) k.setAttribute('data-cursor', 'Select'); else k.removeAttribute('data-cursor');
      });
    }

    function tick(now, dt) {
      p = rawProgress();
      const f = 1 - Math.pow(1 - .16, dt / 16.667);
      sp += (p - sp) * f;
      if (Math.abs(p - sp) < .00005) sp = p;
      const q = sp;

      /* film */
      const filmO = 1 - smooth((q - (F - .05)) / .04);
      if (filmO > .002) {
        if (!filmOn) { film.style.visibility = 'visible'; filmOn = true; }
        film.style.opacity = filmO.toFixed(3);
        const idx = Math.round(clamp(q / (F * .92), 0, 1) * (Math.max(1, imgs.length) - 1));
        drawFrame(idx);
        canvas.style.transform = 'scale(' + (1 + .07 * clamp(q / F, 0, 1)).toFixed(4) + ')';
      } else if (filmOn) { film.style.visibility = 'hidden'; filmOn = false; }
      const o1 = 1 - smooth((q - (F - .06)) / .035);
      t1.style.opacity = o1.toFixed(3);
      t1.style.transform = 'translate3d(0,' + (-(1 - o1) * 40).toFixed(1) + 'px,0)';
      const o2 = smooth((q - .03) / .035) * (1 - smooth((q - (F - .055)) / .035));
      t2.style.opacity = o2.toFixed(3);
      t2.style.transform = 'translate3d(0,' + ((1 - smooth((q - .03) / .035)) * 22 - (1 - (1 - smooth((q - (F - .055)) / .035))) * 30).toFixed(1) + 'px,0)';
      const tone = q < F - .02 ? 'light' : 'dark';
      if (sec.getAttribute('data-vpg-tone') !== tone) { sec.setAttribute('data-vpg-tone', tone); if (Nav.refresh) Nav.refresh(); }

      /* stage */
      const so = smooth((q - (F - .004)) / .045);
      if (so > .001) {
        if (stage.style.visibility !== 'visible') {
          stage.style.visibility = 'visible';
          if (!revealAt) revealAt = now;
        }
        stage.style.opacity = so.toFixed(3);
        devWrap.style.transform = 'translate(-50%,-50%) scale(' + (.88 + .12 * easeIO(clamp((q - (F - .004)) / .08, 0, 1))).toFixed(4) + ')';
      } else if (stage.style.visibility === 'visible') {
        stage.style.visibility = 'hidden';
        stage.style.opacity = '0';
        revealAt = 0;
      }
      sec.classList.toggle('is-stage', q >= F);

      /* setups */
      const m = q < S0 ? [null, null, null, null, null] : mappingAt(q);
      applyMapping(m, now);
      setPlay(q >= END + .01);

      /* captions and dots */
      let active = -1;
      setupCaps.forEach((c, i) => {
        const a = S0 + i * SEG - .006, b = i === N - 1 ? END : S0 + (i + 1) * SEG - .006;
        const o = capOpacity(q, a, b, false);
        if (o > .5) active = i;
        c.style.opacity = o.toFixed(3);
        c.style.transform = (W >= 990 ? 'translateY(calc(-50% + ' : 'translateY(calc(0% + ') + ((1 - o) * (q < (a + b) / 2 ? 26 : -26)).toFixed(1) + 'px))';
        c.style.visibility = o > .002 ? 'visible' : 'hidden';
      });
      const endCap = caps[caps.length - 1];
      if (endCap && endCap.getAttribute('data-cap') === 'end') {
        const o = capOpacity(q, END, 1.1, true);
        if (o > .5) active = N;
        endCap.style.opacity = o.toFixed(3);
        endCap.style.transform = (W >= 990 ? 'translateY(calc(-50% + ' : 'translateY(calc(0% + ') + ((1 - o) * 26).toFixed(1) + 'px))';
        endCap.style.visibility = o > .002 ? 'visible' : 'hidden';
      }
      dots.forEach((d, i) => d.classList.toggle('is-on', i === active));

      /* pointer parallax */
      pmx += (mx - pmx) * .06; pmy += (my - pmy) * .06;

      if (stage.style.visibility === 'visible') tiles.forEach((t) => stepTile(t, now));
    }

    /* the playable end: pick a key, then a job */
    keys.forEach((k, i) => k.addEventListener('click', () => {
      if (!play) return;
      sel = i;
      keys.forEach((kk, j) => { kk.classList.toggle('is-sel', j === i); kk.setAttribute('aria-pressed', j === i ? 'true' : 'false'); });
      hit(k);
    }));
    tiles.forEach((t) => t.el.addEventListener('click', () => {
      if (!play || !custom) return;
      const at = custom.indexOf(t.job);
      if (at === sel) { hit(keys[sel]); return; }
      const prev = custom[sel];
      if (at >= 0) custom[at] = prev;
      custom[sel] = t.job;
    }));
    if (FINE) {
      sec.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        mx = e.clientX / window.innerWidth - .5; my = e.clientY / window.innerHeight - .5;
      }, { passive: true });
    }

    let ro = null;
    const relayout = () => { layout(); if (setName) { const pr = (pin.clientWidth / Math.max(1, pin.clientHeight)) < .9 ? 'm' : 'd'; if (pr !== setName) loadFrames(); } };
    if ('ResizeObserver' in window) { ro = new ResizeObserver(relayout); ro.observe(pin); } else window.addEventListener('resize', relayout);
    layout();
    tiles.forEach((t) => { t.x = t.home.x; t.y = t.home.y; t.s = t.home.s; t.o = t.home.o; });
    const io1 = onScreen(sec, loadFrames, null, 0, '200% 0px 200% 0px');
    const io2 = onScreen(sec, () => { if (!visible) { visible = true; sp = rawProgress(); Ticker.add(tick); } }, () => { visible = false; Ticker.remove(tick); }, 0, '60px 0px 60px 0px');
    return { destroy() { Ticker.remove(tick); if (io1) io1.disconnect(); if (io2) io2.disconnect(); if (ro) ro.disconnect(); } };
  }

  /* ================================================ everyday: demos */
  const D_WORDS = [['um', 1], ['so', 1], ['the'], ['deck'], ['is,'], ['uh', 1], ['ready.'], ['Can'], ['you'], ['check'], ['slide'], ['four'], ['by'], ['Friday?'], ['Thanks!']];
  const D_CLEAN = 'The deck is ready. Can you check slide 4 by Friday? Thanks!';

  function DictateDemo(pane, api) {
    const out = $('[data-out]', pane), raw = $('[data-raw]', pane), hud = $('[data-hud]', pane);
    function reset() {
      out.textContent = ''; raw.textContent = '';
      hud.className = 'vpg-hud';
      pane.classList.remove('is-typing', 'is-ready');
      api.hold(false);
    }
    function addWord(w) {
      const s = doc.createElement('span');
      s.className = 'w' + (w[1] ? ' is-fill' : '');
      s.textContent = w[0];
      raw.appendChild(s);
    }
    async function finish(tok) {
      api.hold(false);
      hud.classList.remove('is-live');
      $$('.w.is-fill', raw).forEach((w) => w.classList.add('is-gone'));
      await wait(tok, 600);
      hud.classList.add('is-done');
      await wait(tok, 650);
      hud.classList.remove('is-on');
      pane.classList.add('is-typing');
      await typeText(tok, out, D_CLEAN, 24);
    }
    async function script(tok) {
      pane.classList.add('is-ready');
      await wait(tok, 700);
      api.hold(true);
      hud.classList.add('is-on', 'is-live');
      for (const w of D_WORDS) { addWord(w); await wait(tok, w[1] ? 320 : 205); }
      await wait(tok, 320);
      await finish(tok);
      await wait(tok, 2600);
    }
    const d = Scripted(reset, script, 10200);
    /* the visitor holds the wide key */
    const manual = new Runner();
    let holding = false, said = 0;
    d.holdStart = () => {
      d.run.stop(); reset();
      holding = true; said = 0;
      pane.classList.add('is-ready');
      api.hold(true);
      hud.classList.add('is-on', 'is-live');
      const tok = manual.token();
      (async () => {
        try { while (holding && said < D_WORDS.length) { addWord(D_WORDS[said++]); await wait(tok, 225); } } catch (e) { swallow(e); }
      })();
    };
    d.holdEnd = (done) => {
      if (!holding) return;
      holding = false;
      const tok = manual.token();
      (async () => {
        try {
          while (said < D_WORDS.length) { addWord(D_WORDS[said++]); await wait(tok, 40); }
          await wait(tok, 180);
          await finish(tok);
          await wait(tok, 3600);
          if (done) done();
        } catch (e) { swallow(e); }
      })();
    };
    d.cancelHold = () => { holding = false; manual.stop(); };
    d.isHolding = () => holding;
    d.final = () => { pane.classList.add('is-ready'); out.textContent = D_CLEAN; };
    return d;
  }

  function MeetingDemo(pane, api) {
    const win = $('.vpg-meet', pane), clock = $('[data-clock]', pane);
    const lines = $$('[data-line]', pane), blocks = $$('[data-n]', pane), todos = $$('[data-todo]', pane);
    const fmt = (s) => { const m = Math.floor(s / 60), r = Math.floor(s % 60); return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r; };
    function reset() {
      win.classList.remove('is-notes', 'is-live');
      lines.concat(blocks).forEach((l) => l.classList.remove('is-in'));
      todos.forEach((t) => t.classList.remove('is-done'));
      clock.textContent = '00:00';
      api.hold(false);
    }
    async function script(tok) {
      await wait(tok, 800);
      await api.press(tok);
      win.classList.add('is-live');
      const t0 = performance.now(), RUN = 5400, END = 31 * 60 + 12;
      const timer = setInterval(() => {
        if (!tok.alive()) { clearInterval(timer); return; }
        const q = clamp((performance.now() - t0) / RUN, 0, 1);
        clock.textContent = fmt(END * (q * q * .35 + q * .65));
      }, 60);
      try {
        await wait(tok, 650); lines[0].classList.add('is-in');
        await wait(tok, 1450); lines[1].classList.add('is-in');
        await wait(tok, 1450); lines[2].classList.add('is-in');
        await wait(tok, 1850);
      } finally { clearInterval(timer); }
      clock.textContent = fmt(END);
      await api.press(tok);
      win.classList.remove('is-live');
      await wait(tok, 600);
      win.classList.add('is-notes');
      for (const b of blocks) { b.classList.add('is-in'); await wait(tok, 210); }
      await wait(tok, 450);
      for (const t of todos) { t.classList.add('is-done'); await wait(tok, 600); }
      await wait(tok, 2600);
    }
    const d = Scripted(reset, script, 15600);
    d.final = () => { win.classList.add('is-notes'); blocks.forEach((b) => b.classList.add('is-in')); todos[0].classList.add('is-done'); };
    return d;
  }

  function MemoDemo(pane, api) {
    const rec = $('[data-rec]', pane), live = $('[data-live]', pane), item = $('[data-new]', pane), when = $('[data-when]', pane);
    const later = $('[data-later]', pane), ask = $('[data-ask]', pane), q = $('[data-q]', pane), answer = $('[data-answer]', pane);
    const MEMO = "Book Luca's for Mom's birthday, the window table.";
    const QUESTION = 'Where did I want to take Mom for her birthday?';
    function reset() {
      rec.classList.remove('is-on', 'is-live'); live.textContent = '';
      item.classList.remove('is-in', 'is-hi'); when.textContent = 'Memo, just now';
      later.classList.remove('is-on');
      ask.classList.remove('is-focus', 'is-typing'); q.textContent = '';
      answer.classList.remove('is-open');
      api.hold(false);
    }
    async function script(tok) {
      await wait(tok, 800);
      await api.press(tok);
      rec.classList.add('is-on', 'is-live');
      const words = MEMO.split(' ');
      for (let i = 1; i <= words.length; i++) { live.textContent = words.slice(0, i).join(' '); await wait(tok, 235); }
      await wait(tok, 450);
      await api.press(tok);
      rec.classList.remove('is-live');
      await wait(tok, 250);
      rec.classList.remove('is-on');
      item.classList.add('is-in', 'is-hi');
      await wait(tok, 1400);
      item.classList.remove('is-hi');
      await wait(tok, 350);
      later.classList.add('is-on');
      await wait(tok, 700);
      when.textContent = 'Memo, 2 weeks ago';
      await wait(tok, 850);
      later.classList.remove('is-on');
      await wait(tok, 350);
      ask.classList.add('is-focus');
      await wait(tok, 300);
      ask.classList.add('is-typing');
      await typeText(tok, q, QUESTION, 30);
      await wait(tok, 420);
      ask.classList.remove('is-focus');
      answer.classList.add('is-open');
      item.classList.add('is-hi');
      await wait(tok, 3200);
    }
    const d = Scripted(reset, script, 16200);
    d.final = () => {
      item.classList.add('is-in', 'is-hi'); when.textContent = 'Memo, 2 weeks ago';
      ask.classList.add('is-typing'); q.textContent = QUESTION; answer.classList.add('is-open');
    };
    return d;
  }

  function Everyday(sec) {
    const tabs = $$('.vpg-ev__tab', sec), panels = $$('.vpg-ev__panel', sec), panes = $$('.vpg-ev__pane', sec);
    const dev = $('.vpg-ev__dev .vpg-dev', sec);
    const keys = dev ? $$('.vpg-key', dev) : [];
    const hint = $('[data-hint]', sec);
    const tabKey = tabs.map((t) => clamp((parseInt(t.getAttribute('data-key'), 10) || 1) - 1, 0, 4));
    const caps = tabs.map((t) => $('.vpg-cap', t));
    let cur = 0, visible = false;
    const makers = { dictate: DictateDemo, meeting: MeetingDemo, memo: MemoDemo };
    const demos = panes.map((pane, i) => {
      const mk = makers[pane.getAttribute('data-demo')];
      if (!mk) return null;
      return mk(pane, {
        press: async (tok, ms) => {
          const k = keys[tabKey[i]];
          if (k) k.classList.add('is-down');
          if (caps[i]) caps[i].classList.add('is-down');
          try { await wait(tok, ms || 260); } finally {
            if (k) k.classList.remove('is-down');
            if (caps[i]) caps[i].classList.remove('is-down');
          }
        },
        hold: (on) => {
          const k = keys[tabKey[i]];
          if (k) k.classList.toggle('is-down', on);
          if (caps[i]) caps[i].classList.toggle('is-down', on);
        }
      });
    });

    function timing(i, on, paused) {
      tabs.forEach((t) => t.classList.remove('is-timing', 'is-paused'));
      if (!on || REDUCE) return;
      const t = tabs[i], d = demos[i];
      t.style.setProperty('--dur', ((d && d.duration) || 9000) + 'ms');
      void t.offsetWidth;
      t.classList.add('is-timing');
      if (paused) t.classList.add('is-paused');
    }
    function labels() {
      keys.forEach((k, j) => {
        const tab = tabKey.indexOf(j);
        if (tab === cur) k.setAttribute('data-cursor', panes[cur].getAttribute('data-demo') === 'dictate' ? 'Hold' : 'Press');
        else if (tab >= 0) k.setAttribute('data-cursor', 'Show');
        else k.removeAttribute('data-cursor');
        k.classList.toggle('is-lit', j === tabKey[cur]);
        k.setAttribute('aria-pressed', j === tabKey[cur] ? 'true' : 'false');
      });
    }
    function start(i) {
      const d = demos[i];
      if (!d) return;
      if (REDUCE) { d.final && d.final(); return; }
      timing(i, true);
      d.play(() => { if (visible && cur === i) select((i + 1) % tabs.length, false); });
    }
    function select(i, focus) {
      if (demos[cur] && demos[cur].cancelHold) demos[cur].cancelHold();
      cur = i;
      tabs.forEach((t, j) => {
        const on = j === i;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panels.forEach((p, j) => { p.hidden = j !== i; });
      panes.forEach((p, j) => p.classList.toggle('is-on', j === i));
      if (hint) hint.classList.toggle('is-off', panes[i].getAttribute('data-demo') !== 'dictate');
      demos.forEach((d, j) => { if (d && j !== i) d.stop(); });
      labels();
      if (focus) tabs[i].focus();
      if (visible) start(i); else timing(i, false);
    }
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(i, false));
      t.addEventListener('keydown', (e) => {
        const n = tabs.length;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); select((i + 1) % n, true); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); select((i - 1 + n) % n, true); }
        else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
        else if (e.key === 'End') { e.preventDefault(); select(n - 1, true); }
      });
    });

    /* the device keys: show their job, or hold the wide key to dictate */
    keys.forEach((k, j) => {
      const tab = tabKey.indexOf(j);
      if (tab < 0) k.tabIndex = -1;
      const isDictate = () => tab === cur && panes[cur].getAttribute('data-demo') === 'dictate';
      const down = (e) => {
        if (!isDictate() || REDUCE) return;
        if (e.button > 0) return;
        if (e.preventDefault) e.preventDefault();
        if (k.setPointerCapture && e.pointerId != null) { try { k.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ } }
        timing(cur, true, true);
        demos[cur].holdStart();
      };
      const up = () => {
        const d = demos[cur];
        if (!d || !d.isHolding || !d.isHolding()) return;
        d.holdEnd(() => { if (visible && panes[cur].getAttribute('data-demo') === 'dictate') select((cur + 1) % tabs.length, false); });
      };
      k.addEventListener('pointerdown', down);
      k.addEventListener('pointerup', up);
      k.addEventListener('pointercancel', up);
      k.addEventListener('lostpointercapture', up);
      k.addEventListener('contextmenu', (e) => { if (isDictate()) e.preventDefault(); });
      k.addEventListener('keydown', (e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat && isDictate()) { e.preventDefault(); down({ button: 0 }); }
      });
      k.addEventListener('keyup', (e) => { if ((e.key === ' ' || e.key === 'Enter') && isDictate()) { e.preventDefault(); up(); } });
      k.addEventListener('click', () => {
        if (tab < 0 || isDictate()) { hit(k); return; }
        if (tab === cur) { demos[cur].stop(); start(cur); } else select(tab, false);
      });
    });

    labels();
    if (REDUCE) { demos.forEach((d) => d && d.final && d.final()); }
    const io = onScreen(sec.querySelector('.vpg-ev__stage') || sec, () => {
      if (visible) return;
      visible = true;
      start(cur);
    }, () => {
      visible = false;
      demos.forEach((d) => { if (d) { if (d.cancelHold) d.cancelHold(); d.stop(); } });
      timing(cur, false);
    }, .35);
    return { destroy() { visible = false; demos.forEach((d) => d && d.stop()); if (io) io.disconnect(); } };
  }

  /* ======================================================= your AI */
  const AI_PROMPTS = [
    'Draft a friendly reply saying Thursday at 3 works for me.',
    'Summarize this contract and list the three biggest risks.',
    'Plan three days in Kyoto with one great dinner each night.',
    'What are people saying about the launch today?'
  ];
  function AiDemo(sec) {
    const box = $('[data-vpg-demo="ai"]', sec);
    if (!box) return null;
    const keys = $$('.vpg-aikey', sec), name = $('[data-ai-name]', box), opened = $('[data-opened]', box);
    const hello = $('[data-hello]', box), msg = $('[data-msg]', box), sent = $('[data-sent]', box), reply = $('[data-reply]', box);
    const input = $('[data-input]', box), typed = $('[data-typed]', box);
    const names = keys.map((k) => $('.vpg-aikey__name', k).textContent.trim());
    let at = 0;
    function clearChat() {
      hello.classList.remove('is-gone'); msg.classList.remove('is-on'); reply.classList.remove('is-on');
      typed.textContent = '';
      input.classList.remove('is-live', 'is-typing', 'is-ready');
    }
    function reset() {
      clearChat();
      keys.forEach((k) => { k.classList.remove('is-lit'); k.setAttribute('aria-pressed', 'false'); });
      opened.classList.remove('is-on');
    }
    async function show(tok, i) {
      keys.forEach((k, j) => { k.classList.toggle('is-lit', j === i); k.setAttribute('aria-pressed', j === i ? 'true' : 'false'); });
      opened.classList.remove('is-on');
      name.classList.add('is-swap');
      await wait(tok, 200);
      clearChat();
      name.textContent = names[i];
      name.classList.remove('is-swap');
      await wait(tok, 250);
      opened.classList.add('is-on');
      await wait(tok, 420);
      input.classList.add('is-live', 'is-typing');
      await typeText(tok, typed, AI_PROMPTS[i % AI_PROMPTS.length], 25);
      input.classList.add('is-ready');
      await wait(tok, 480);
      input.classList.remove('is-live', 'is-typing', 'is-ready');
      sent.textContent = typed.textContent;
      typed.textContent = '';
      hello.classList.add('is-gone');
      msg.classList.add('is-on');
      await wait(tok, 520);
      reply.classList.add('is-on');
      await wait(tok, 2200);
      keys[i].classList.remove('is-lit');
    }
    async function script(tok) { await show(tok, at); at = (at + 1) % keys.length; await wait(tok, 240); }
    function still(i) {
      reset();
      keys[i].classList.add('is-lit'); keys[i].setAttribute('aria-pressed', 'true');
      name.textContent = names[i]; opened.classList.add('is-on');
      sent.textContent = AI_PROMPTS[i % AI_PROMPTS.length];
      hello.classList.add('is-gone'); msg.classList.add('is-on'); reply.classList.add('is-on');
    }
    const demo = Looping(box, reset, script, () => still(0));
    keys.forEach((k, i) => k.addEventListener('click', () => { at = i; if (REDUCE) still(i); else demo.restart(); }));
    return demo;
  }

  /* ======================================================= Genspark */
  const GS_RUNS = [
    { cmd: 'Make a 6-slide deck on our Q3 results.', out: 'deck', steps: ['Planning the outline', 'Gathering the numbers', 'Building the charts', 'Designing the slides'] },
    { cmd: 'Plan five easy family dinners and a shopping list.', out: 'sheet', steps: ['Picking the recipes', 'Checking the cooking times', 'Building the shopping list', 'Putting it in a sheet'] }
  ];
  function GsDemo(sec) {
    const box = $('[data-vpg-demo="genspark"]', sec);
    if (!box) return null;
    const cmd = $('[data-cmd]', box), go = $('[data-go]', box), steps = $$('[data-step]', box), outs = $$('[data-out]', box), kinds = $$('[data-kind]', box);
    let n = 0;
    function reset() {
      box.classList.remove('is-live', 'is-working');
      cmd.textContent = '';
      go.classList.remove('is-on', 'is-press');
      steps.forEach((s, i) => { s.classList.remove('is-run', 'is-done'); $('[data-step-text]', s).textContent = GS_RUNS[n].steps[i]; });
      outs.forEach((o) => o.classList.remove('is-on'));
      kinds.forEach((k) => k.classList.toggle('is-on', k.getAttribute('data-kind') === GS_RUNS[n].out));
    }
    async function script(tok) {
      const run = GS_RUNS[n];
      await wait(tok, 600);
      box.classList.add('is-live');
      await typeText(tok, cmd, run.cmd, 30);
      box.classList.remove('is-live');
      go.classList.add('is-on');
      await wait(tok, 360);
      go.classList.add('is-press');
      await wait(tok, 160);
      go.classList.remove('is-press');
      box.classList.add('is-working');
      for (let i = 0; i < steps.length; i++) {
        steps[i].classList.add('is-run');
        await wait(tok, 620 + i * 60);
        steps[i].classList.remove('is-run');
        steps[i].classList.add('is-done');
      }
      box.classList.remove('is-working');
      outs.forEach((o) => o.classList.toggle('is-on', o.getAttribute('data-out') === run.out));
      await wait(tok, 3800);
      n = (n + 1) % GS_RUNS.length;
    }
    return Looping(box, reset, script, () => {
      cmd.textContent = GS_RUNS[0].cmd; go.classList.add('is-on');
      steps.forEach((s) => s.classList.add('is-done'));
      outs.forEach((o) => o.classList.toggle('is-on', o.getAttribute('data-out') === 'deck'));
    });
  }

  /* ===================================================== top bar */
  function Nav() {
    const bar = $('[data-vpg-nav]');
    if (!bar || bar._vpg) return;
    bar._vpg = 1;
    const prog = $('.vpg-nav__progress', bar);
    let on = false, tone = '', queued = false;
    function check() {
      queued = false;
      const y = window.scrollY || window.pageYOffset;
      const want = y > 8;
      if (want !== on) { on = want; bar.classList.toggle('is-solid', on); }
      const mid = bar.offsetHeight / 2;
      /* the last match wins: later sections slide over earlier ones (the sheets) */
      let t = '';
      $$('[data-vpg-tone]').forEach((s) => {
        const r = s.getBoundingClientRect();
        if (r.top <= mid && r.bottom > mid) t = s.getAttribute('data-vpg-tone');
      });
      if (t !== tone) {
        bar.classList.toggle('is-dark', t === 'dark');
        bar.classList.toggle('is-blue', t === 'blue');
        tone = t;
      }
      if (prog) {
        const max = Math.max(1, root.scrollHeight - window.innerHeight);
        prog.style.setProperty('--p', clamp(y / max, 0, 1).toFixed(4));
      }
    }
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(check); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    Ticker.add(function watch() { check(); Ticker.remove(watch); });
    check();
    Nav.refresh = queue;
  }

  /* ======================================== pointer label (desktop) */
  function Cursor() {
    if (!FINE || REDUCE || $('.vpg-cursor')) return;
    const el = doc.createElement('div');
    el.className = 'vpg-cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<span></span>';
    doc.body.appendChild(el);
    const label = el.firstChild;
    let x = 0, y = 0, tx = 0, ty = 0, on = false, running = false;
    function tick() {
      x += (tx - x) * .35; y += (ty - y) * .35;
      el.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
      if (!on && Math.abs(tx - x) < .3 && Math.abs(ty - y) < .3) { Ticker.remove(tick); running = false; }
    }
    doc.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      tx = e.clientX; ty = e.clientY;
      const t = e.target && e.target.closest ? e.target.closest('[data-cursor]') : null;
      const txt = t && t.getAttribute('data-cursor');
      if (txt) {
        if (label.textContent !== txt) label.textContent = txt;
        if (!on) { on = true; x = tx; y = ty; el.classList.add('is-on'); }
        el.classList.toggle('is-dark', !!t.closest('.vpg-dark, .vpg-ch, .vpg-ai'));
      } else if (on) { on = false; el.classList.remove('is-on'); }
      if (!running) { running = true; Ticker.add(tick); }
    }, { passive: true });
    doc.addEventListener('pointerleave', () => { on = false; el.classList.remove('is-on'); });
  }

  /* ============================================================ wiring */
  let registry = [];
  /* "Why not just use your laptop?": each row lights its key once it is in view */
  function Why(sec) {
    const rows = $$('.vpg-why__row', sec);
    if (REDUCE || !('IntersectionObserver' in window)) { rows.forEach((r) => r.classList.add('is-in')); return null; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.6 });
    rows.forEach((r) => io.observe(r));
    return { destroy: () => io.disconnect() };
  }

  function init(scope) {
    scope = scope || doc;
    Nav();
    Cursor();
    reveals(scope);
    let heroField = null, endField = null;
    $$('[data-vpg-field]', scope).forEach((el) => {
      if (el._vpg) return;
      el._vpg = 1;
      const f = new Field(el);
      registry.push({ el, obj: f });
      if (el.getAttribute('data-vpg-field') === 'hero') heroField = f;
      if (el.getAttribute('data-vpg-field') === 'end') endField = f;
    });
    $$('[data-vpg-hero]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Hero(el, heroField) }); });
    $$('[data-vpg-chapter]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Chapter(el) }); });
    $$('[data-vpg-ev]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Everyday(el) }); });
    $$('.vpg-ai', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: AiDemo(el) }); });
    $$('.vpg-gsp', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: GsDemo(el) }); });
    $$('[data-vpg-why]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Why(el) }); });
    const done = $('[data-vpg-done]', scope);
    if (done && endField) {
      setTimeout(() => { const r = done.getBoundingClientRect(); endField.ripple(r.left + 24, r.top + r.height / 2, true); }, 700);
    }
    if (Nav.refresh) Nav.refresh();
  }
  function teardown(scope) {
    registry = registry.filter((r) => {
      if (scope.contains(r.el)) { if (r.obj && r.obj.destroy) r.obj.destroy(); return false; }
      return true;
    });
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => init(doc));
  else init(doc);
  doc.addEventListener('shopify:section:load', (e) => init(e.target));
  doc.addEventListener('shopify:section:unload', (e) => teardown(e.target));
})();
